/**
 * ProjectHeader — rich project sub-header matching designs/Board.html `.ph-top`.
 *
 * Renders the project name, owner avatar+name, optional due date with calendar
 * icon, a status pill, and a progress bar with count label. Designed to sit
 * inside `.proj-head` above the tab strip on the board/list views.
 *
 * Usage:
 *   <ProjectHeader project={{ id, name, ownerName, ownerAvatarUrl, dueDate,
 *                              completedCount, totalCount, status }} />
 */

import { Avatar } from "@/components/Avatar";
import { Icon } from "@/components/Icon";
import { TagChip } from "@/components/TagChip";
import { Pill } from "@/components/Pill";
import { ProgressBar } from "@/components/ProgressBar";
import { PROJECT_STATUS_LABEL, projectPillVariant } from "@/lib/projectStatus";

/** Shape of the project data expected by ProjectHeader. */
export type ProjectHeaderProject = {
  id: string;
  name: string;
  ownerName: string;
  /** Avatar image URL — reserved for future use; Avatar renders initials today. */
  ownerAvatarUrl?: string | null;
  /** Unix epoch seconds, or null when no due date is set. */
  dueDate: number | null;
  completedCount: number;
  totalCount: number;
  status: "active" | "on_hold" | "archived";
  /** T46: project tags rendered as chips next to the status pill. */
  tags?: { id: string; name: string; color: string }[];
};

export type ProjectHeaderProps = {
  project: ProjectHeaderProject;
  /** When provided, renders an owner-only pencil button next to the title. */
  onEdit?: () => void;
};

/** Formats a unix epoch seconds value as "Jun 14" style. */
function formatDueDate(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/**
 * Renders the rich project header strip: name, owner, due date, status pill,
 * and progress bar with count label.
 */
export function ProjectHeader({ project: p, onEdit }: ProjectHeaderProps) {
  return (
    <div className="ph-top">
      <div className="ph-meta">
        <div className="ph-title-row">
          <h1 className="t-h1">{p.name}</h1>
          {onEdit ? (
            <button
              type="button"
              className="ph-edit"
              aria-label="Edit project"
              title="Edit project"
              onClick={onEdit}
            >
              <Icon name="pencil" size={15} />
            </button>
          ) : null}
        </div>
        <div className="ph-row">
          <span className="ph-owner">
            <Avatar
              id={p.id}
              name={p.ownerName}
              size={20}
              image={p.ownerAvatarUrl}
            />
            <span className="t-sm">{p.ownerName}</span>
          </span>
          {p.dueDate ? (
            <span className="ph-due" aria-label="Due date">
              <Icon name="cal" size={14} />
              <span className="t-sm">{formatDueDate(p.dueDate)}</span>
            </span>
          ) : null}
          <Pill variant={projectPillVariant(p.status)} dot>
            {PROJECT_STATUS_LABEL[p.status] ?? p.status}
          </Pill>
          {(p.tags ?? []).map((t) => (
            <TagChip key={t.id} name={t.name} color={t.color} />
          ))}
        </div>
      </div>
      <div className="ph-progress">
        <ProgressBar
          value={p.completedCount}
          total={p.totalCount}
          label="count"
          ariaLabel="Project progress"
        />
      </div>
    </div>
  );
}
